import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import "../css/bookingForm.css";
import Nav from "./nav";
import { FiCheckCircle, FiArrowRight, FiCalendar, FiUser, FiBriefcase, FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import Swal from 'sweetalert2';

const BookingForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role") || "delegate";
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    company: "",
    designation: "",
    email: "",
    telephone: "",
    comment: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Get the role-specific email from getRoleContent()
    const { roleEmail } = getRoleContent();
    
    try {
      const response = await fetch("https://enerygx-backend.onrender.com/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          role, // Send the role to backend
          adminEmail: roleEmail // Send the role-specific email
        }),
      });
  
      const data = await response.json();
      
      if(response.ok){

        if (role == "delegate") {
          await Swal.fire({
            title: 'Success!',
            text: `Your interest for Delegate registration has been submitted successfully. Our team will be in touch shortly`,
            icon: 'success',
            confirmButtonText: 'Continue',
            confirmButtonColor: '#0056b3',
          });

        } else if (role == "exhibitor"){
          await Swal.fire({
            title: 'Success!',
            text: `Your interest for Exhibition registration has been submitted successfully. Our team will be in touch shortly`,
            icon: 'success',
            confirmButtonText: 'Continue',
            confirmButtonColor: '#0056b3',
          });

        }else if (role == "sponsor"){
          await Swal.fire({
            title: 'Success!',
            text: `Your interest for Sponsorship registration has been submitted successfully. Our team will be in touch shortly`,
            icon: 'success',
            confirmButtonText: 'Continue',
            confirmButtonColor: '#0056b3',
          });

        }
        
         
        // Reset form
        setFormData({
          name: "",
          surname: "",
          company: "",
          designation: "",
          email: "",
          telephone: "",
          comment: "",
        });
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (error) {
      // Show error alert
      await Swal.fire({
        title: 'Error!',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Try Again',
        confirmButtonColor: '#d33',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleContent = () => {
    switch (role) {
      case "exhibitor":
        return {
          headline: "Showcase at Africa's Premier Energy Event",
          description: [
            "The EnergyX Africa Conference & Exhibition is the vibrant nucleus of the EnergyX - a high-energy, interactive arena where over 500+ renewable energy leaders converge.",
            "This is your gateway to spotlight your organization's role in Southern Africa's renewable energy revolution."
          ],
          sections: [
            {
              title: "Why Exhibit?",
              points: [
                "Strategic Networking with government officials and industry leaders",
                "Brand Amplification at Southern Africa's premier energy event",
                "Market Leadership positioning for your solutions"
              ]
            },
            {
              title: "Exhibition Packages",
              points: [
                "Tailored packages for maximum impact",
                "Premium placement opportunities",
                "Spaces starting at ZAR 11,500"
              ]
            }
          ],
          contactText: "For inquiries or to reserve your space: exhibition@energyx.africa",
          roleEmail:"exhibition@energyx.africa"
        };
      case "sponsor":
        return {
          headline: "Step Up. Lead. Transform.",
          description: [
            "Align your organization with Southern Africa's most influential renewable energy pioneers.",
            "Demonstrate your commitment to climate leadership and cutting-edge innovation."
          ],
          sections: [
            {
              title: "Why Partner With Us?",
              points: [
                "Strategic Visibility with 500+ policymakers and investors",
                "Thought Leadership showcasing your innovations",
                "Market Access to key projects and programs"
              ]
            },
            {
              title: "Sponsorship Opportunities",
              points: [
                "High-impact branding throughout the conference",
                "Curated networking platforms",
                "Custom partnership packages"
              ]
            }
          ],
          contactText: "Contact our team to craft a bespoke package: connect@energyx.africa",
          roleEmail:"connect@energyx.africa"
        };
      case "delegate":
      default:
        return {
          headline: "Join Africa's Energy Revolution",
          description: [
            "Engage with leaders driving innovation in renewable energy and just energy transition.",
            "Connect and collaborate with pioneers shaping Africa's sustainable energy future."
          ],
          sections: [
            {
              title: "Delegate Benefits",
              points: [
                "Access to 500+ energy leaders and policymakers",
                "Exclusive networking opportunities",
                "First-hand industry insights"
              ]
            },
            {
              title: "Why Attend?",
              points: [
                "Advance your renewable energy commitment",
                "Gain strategic knowledge",
                "Position your organization at the forefront"
              ]
            }
          ],
          contactText: "We offer flexible packages: conference@energyx.africa",
          roleEmail:"conference@energyx.africa"
        };
    }
  };

  const { headline, description, sections, contactText } = getRoleContent();

  return (
    <div className="booking-page">
      <Nav />

      {/* Hero Section */}
      <div className="booking-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="role-badge">{role.charAt(0).toUpperCase() + role.slice(1)}</div>
          <h1>{headline}</h1>
          <div className="event-details">
            {/* <span className="date"><FiCalendar /> September 15 - 17, 2025</span> */}
            <span className="location"> Coming in 2026 to the Mittah Seperepere International Convention Centre, Kimberley, South Africa.</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="booking-container">
        <div className="booking-content">
          <div className="content-section">
            {description.map((paragraph, index) => (
              <p key={index} className="description-text">{paragraph}</p>
            ))}

            <div className="benefits-section">
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="benefit-card">
                  <h3 className="section-title">{section.title}</h3>
                  <ul className="benefit-list">
                    {section.points.map((point, pointIndex) => (
                      <li key={pointIndex}>
                        <FiCheckCircle className="check-icon" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="contact-card">
              <p className="contact-text">{contactText}</p>
            </div>
          </div>

          {/* Registration Form */}
          <div className="registration-form">
            <div className="form-header">
              <h2>Registration Form</h2>
              <p>Complete this form to secure your participation</p>
            </div>

            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Enter Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    id="surname"
                    name="surname"
                    placeholder="Enter your Surname"
                    value={formData.surname}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Enter your Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    id="designation"
                    name="designation"
                    placeholder="Enter Your Position"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    placeholder="Enter your number"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <textarea
                  id="comment"
                  name="comment"
                  placeholder="Any special requirements or comments..."
                  value={formData.comment}
                  onChange={handleInputChange}
                  rows="4"
                ></textarea>
              </div>

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    Submit Registration <FiArrowRight className="arrow-icon" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;